import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services'
import { queryKeys, invalidateQueries } from '@/lib/query'
import { useOrg } from '@/context/OrgContext'
import type { ComplianceTarget, Room } from '@/types'

interface CreateRoomData {
  building_id: string
  name: string
  floor?: string
}

interface UpdateRoomData {
  name?: string
  floor?: string
  /**
   * Compliance target override for this room.
   * - omitted (undefined) → don't touch the stored target
   * - null               → clear the stored target (inherit from building / org)
   * - object             → set / replace the stored target
   */
  target?: ComplianceTarget | null
}

/**
 * Hook to fetch all rooms for a building.
 */
export function useRooms(buildingId: string) {
  return useQuery({
    queryKey: queryKeys.rooms.all(buildingId),
    queryFn: () => api.get<Room[]>(`/buildings/${buildingId}/rooms`),
    enabled: !!buildingId,
  })
}

/**
 * Hook to fetch all rooms across the organization.
 */
export function useAllRooms() {
  const { effectiveOrgId } = useOrg()
  return useQuery({
    queryKey: ['rooms', 'all'] as const,
    queryFn: () => api.get<Room[]>('/rooms'),
    enabled: !!effectiveOrgId,
  })
}

/**
 * Hook to fetch a single room by ID.
 */
export function useRoom(roomId: string) {
  return useQuery({
    queryKey: queryKeys.rooms.detail(roomId),
    queryFn: () => api.get<Room>(`/rooms/${roomId}`),
    enabled: !!roomId,
  })
}

/**
 * Hook to create a new room.
 */
export function useCreateRoom(buildingId: string) {
  return useMutation({
    mutationFn: (data: CreateRoomData) => api.post<Room>('/rooms', data),
    onSuccess: () => {
      invalidateQueries.rooms(buildingId)
    },
  })
}

/**
 * Hook to update a room.
 */
export function useUpdateRoom(roomId: string, buildingId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateRoomData) => api.put<Room>(`/rooms/${roomId}`, data),
    onSuccess: (updatedRoom) => {
      queryClient.setQueryData(queryKeys.rooms.detail(roomId), updatedRoom)
      invalidateQueries.rooms(buildingId)
    },
  })
}

/**
 * Hook to delete a room.
 */
export function useDeleteRoom(buildingId: string) {
  return useMutation({
    mutationFn: (roomId: string) => api.delete<void>(`/rooms/${roomId}`),
    onSuccess: () => {
      invalidateQueries.rooms(buildingId)
    },
  })
}
